import { z } from 'zod';
import { paginatedSchema } from './api-error.js';

/**
 * Mail providers the monitor can ingest from.
 *
 * @since 1.0.0
 */
export const MAIL_PROVIDERS = ['mailgun', 'ses'] as const;

export const mailProviderSchema = z.enum(MAIL_PROVIDERS);

export type MailProviderName = z.infer<typeof mailProviderSchema>;

/**
 * The two hosts Mailgun serves its API from. An account lives in exactly one region,
 * and its domains, events and suppressions are only visible from that region's host.
 *
 * @since 1.2.0
 */
export const MAILGUN_API_BASES = {
    us: 'https://api.mailgun.net',
    eu: 'https://api.eu.mailgun.net',
} as const;

export type MailgunRegion = keyof typeof MAILGUN_API_BASES;

/**
 * The API base used when an account does not name one. Mailgun has a US and an EU
 * host; SES is regional, and the region is read back out of this URL.
 *
 * @since 1.1.0
 */
export const DEFAULT_API_BASES: Record<MailProviderName, string> = {
    mailgun: MAILGUN_API_BASES.us,
    ses: 'https://email.us-east-1.amazonaws.com',
};

/**
 * Suffix on an SES account's secret name under which its secret access key is
 * stored. `SES_ACCESS_KEY` holds the access key id and `SES_ACCESS_KEY_SECRET` the
 * secret, so one row still names one credential.
 *
 * @since 1.1.0
 */
export const SES_SECRET_SUFFIX = '_SECRET';

/**
 * Suffix on a Mailgun account's secret name under which its HTTP webhook signing key
 * is stored. Each Mailgun account signs with its own key, so `MAILGUN_API_KEY` pairs
 * with `MAILGUN_API_KEY_SIGNING_KEY`.
 *
 * @since 1.2.0
 */
export const MAILGUN_SIGNING_KEY_SUFFIX = '_SIGNING_KEY';

/**
 * The second secret each provider reads from its account's secret name plus a suffix.
 *
 * @since 1.2.0
 */
export const PROVIDER_COMPANION_SUFFIXES: Record<MailProviderName, string> = {
    mailgun: MAILGUN_SIGNING_KEY_SUFFIX,
    ses: SES_SECRET_SUFFIX,
};

/**
 * Drops trailing slashes, which would otherwise turn every request path into `//v3/...`.
 *
 * @since 1.2.0
 * @param apiBase - the API base as entered
 * @returns the API base without trailing slashes
 */
export function normalizeApiBase(apiBase: string): string {
    return apiBase.trim().replace(/\/+$/, '');
}

/**
 * Reads the AWS region out of an SESv2 endpoint such as
 * `https://email.us-east-1.amazonaws.com`.
 *
 * @since 1.1.0
 * @param apiBase - the account's API base
 * @returns the region, or null when the base is not a regional SES endpoint
 */
export function sesRegionOf(apiBase: string): string | null {
    const match = apiBase.match(/^https:\/\/email\.([a-z0-9-]+)\.amazonaws\.com(\.cn)?\/?$/);
    return match?.[1] ?? null;
}

/**
 * Reads the Mailgun region out of an API base.
 *
 * @since 1.2.0
 * @param apiBase - the account's API base
 * @returns the region, or null when the base is not one of Mailgun's API hosts
 */
export function mailgunRegionOf(apiBase: string): MailgunRegion | null {
    const match = apiBase.match(/^https:\/\/api(\.eu)?\.mailgun\.net\/?$/);
    if (!match) {
        return null;
    }
    return match[1] ? 'eu' : 'us';
}

/**
 * Per-provider API base validation, one message each so the dialog and the API
 * refuse the same values with the same words.
 *
 * @since 1.2.0
 */
const API_BASE_RULES: Record<MailProviderName, { isValid: (apiBase: string) => boolean; message: string }> = {
    mailgun: {
        isValid: (apiBase) => mailgunRegionOf(apiBase) !== null,
        message: `A Mailgun API base must be ${MAILGUN_API_BASES.us} (US) or ${MAILGUN_API_BASES.eu} (EU).`,
    },
    ses: {
        isValid: (apiBase) => sesRegionOf(apiBase) !== null,
        message: 'An SES API base must be a regional endpoint such as https://email.us-east-1.amazonaws.com.',
    },
};

/**
 * Explains why an API base cannot be used with a provider.
 *
 * @since 1.2.0
 * @param provider - the provider the account belongs to
 * @param apiBase - the candidate API base
 * @returns the problem, or null when the base is usable
 */
export function apiBaseProblem(provider: MailProviderName, apiBase: string): string | null {
    const rule = API_BASE_RULES[provider];
    return rule.isValid(apiBase) ? null : rule.message;
}

/**
 * A configured provider account. Credentials never live in this row: `secretName`
 * names the Worker secret that holds them, so multiple Mailgun accounts and
 * subaccounts can coexist without hardcoding any one of them.
 *
 * @since 1.0.0
 */
export const providerAccountSchema = z.object({
    id: z.number().int(),
    provider: mailProviderSchema,
    label: z.string(),
    secretName: z.string(),
    apiBase: z.url().or(z.literal('')),
    isDefault: z.boolean(),
    createdAt: z.number().int(),
});

export type ProviderAccount = z.infer<typeof providerAccountSchema>;

/**
 * How the provider account routes wrap a created or updated account.
 *
 * @since 1.3.0
 */
export const providerAccountEnvelopeSchema = z.object({
    account: providerAccountSchema,
});

export type ProviderAccountEnvelope = z.infer<typeof providerAccountEnvelopeSchema>;

/**
 * One page of the provider account roster.
 *
 * @since 1.3.0
 */
export const providerAccountsPageSchema = paginatedSchema(providerAccountSchema);

export type ProviderAccountsPage = z.infer<typeof providerAccountsPageSchema>;

export const createProviderAccountRequestSchema = z.object({
    provider: mailProviderSchema,
    label: z.string().min(1).max(120),
    secretName: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[A-Z][A-Z0-9_]*$/, 'Secret names are upper snake case'),
    apiBase: z.url().optional(),
    isDefault: z.boolean().default(false),
});

export type CreateProviderAccountRequest = z.infer<typeof createProviderAccountRequestSchema>;

/**
 * Changing an existing provider account, one optional field at a time, including the provider itself.
 *
 * @since 1.0.0
 */
export const updateProviderAccountRequestSchema = z
    .object({
        provider: mailProviderSchema.optional(),
        label: z.string().min(1).max(120).optional(),
        secretName: z
            .string()
            .min(1)
            .max(120)
            .regex(/^[A-Z][A-Z0-9_]*$/, 'Secret names are upper snake case')
            .optional(),
        apiBase: z.url().optional(),
        isDefault: z.boolean().optional(),
    })
    .refine((body) => Object.keys(body).length > 0, { message: 'There was nothing to update.' });

export type UpdateProviderAccountRequest = z.infer<typeof updateProviderAccountRequestSchema>;
